/**
 * Tenant auth against api-devtools `/devtools/v1/auth/*`.
 * Google access token → member JWT with team_id. OAuth providers are listed by
 * GET /v1/auth/providers (Google now; more later).
 *
 * Local Mode B: VITE_DEV_AUTH_STUB=1 skips remote auth so a minted JWT can
 * drive the SPA against a local API.
 */

import type { BeatlyAuthData, GoogleUserInfo } from '../types';
import { AuthApiError } from '../types';
import { getDevtoolsApiUrl, isDevelopment } from '../utils/env';
import { withRetry } from '../utils/withRetry';

/** JWT accepted only by the local Vite auth stub — never a real api token. */
export const DEV_STUB_JWT = 'dev-stub-jwt';

/** Historical Beatly staff team (bootstrap / migration default). */
export const DEFAULT_BEATLY_TEAM_ID = 'admin-beatly-tea';

/** True when local Vite opts into the auth stub (Mode B against api-devtools). */
function isDevAuthStubEnabled(): boolean {
  return isDevelopment() && import.meta.env.VITE_DEV_AUTH_STUB === '1';
}

/**
 * Resolve the JWT the local API will accept.
 * Prefer VITE_DEV_STUB_JWT when the UI injects DEV_STUB_JWT.
 */
function resolveDevStubToken(token: string): string {
  if (token === DEV_STUB_JWT) {
    const minted = (import.meta.env.VITE_DEV_STUB_JWT || '').trim();
    return minted || token;
  }
  return token;
}

/**
 * Decode JWT payload claims without verifying (dev stub / team claim peek).
 * Visitor JWTs carry `role=visitor` and a display `name` from admin mint.
 */
function peekJwtPayload(token: string): {
  email: string;
  team_id: string;
  team_ids: string[];
  user_id: string;
  role: string;
  name: string;
} {
  try {
    const part = token.split('.')[1];
    if (!part) {
      return {
        email: 'qa@beatly.com',
        team_id: DEFAULT_BEATLY_TEAM_ID,
        team_ids: [DEFAULT_BEATLY_TEAM_ID],
        user_id: 'qa',
        role: '',
        name: '',
      };
    }
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as {
      user_id?: string;
      email?: string;
      team_id?: string;
      team_ids?: string[];
      role?: string;
      name?: string;
    };
    let email = payload.email || '';
    const uid = payload.user_id || '';
    if (!email && uid.startsWith('admin-')) email = uid.slice('admin-'.length);
    if (!email) email = uid || 'qa@beatly.com';
    const teamIds = Array.isArray(payload.team_ids)
      ? payload.team_ids.map(String)
      : [];
    const teamId = payload.team_id || teamIds[0] || DEFAULT_BEATLY_TEAM_ID;
    const name = (payload.name || '').trim();
    return {
      email,
      team_id: teamId,
      team_ids: teamIds.length ? teamIds : [teamId],
      user_id: uid || email,
      role: (payload.role || '').trim(),
      name,
    };
  } catch {
    return {
      email: 'qa@beatly.com',
      team_id: DEFAULT_BEATLY_TEAM_ID,
      team_ids: [DEFAULT_BEATLY_TEAM_ID],
      user_id: 'qa',
      role: '',
      name: '',
    };
  }
}

function createAuthError(message: string, status?: number, code?: string): AuthApiError {
  return new AuthApiError(message, status, code);
}

async function parseError(response: Response): Promise<AuthApiError> {
  let message = `API request failed: ${response.status}`;
  let code: string | undefined;
  try {
    const data = (await response.json()) as { message?: string; detail?: string; code?: string };
    message = data.message || data.detail || message;
    code = data.code;
  } catch {
    // Keep the status fallback when the body is not JSON.
  }
  return createAuthError(message, response.status, code);
}

async function postDevtoolsAuth<T>(endpoint: string, body: unknown, token?: string): Promise<T> {
  const apiUrl = getDevtoolsApiUrl();
  return withRetry(async () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as T;
  });
}

export type MemberAuthInfo = {
  name: string;
  email: string;
  team_id: string;
  team_ids: string[];
  user_id: string;
  role: string;
};

export type MemberAuthResult = BeatlyAuthData & {
  team_id: string;
  team_ids: string[];
  user_id: string;
  display_name: string;
  role: string;
};

/**
 * Exchange a Google OAuth2 access token for a DevTools member JWT.
 * Throws AuthApiError with USER_BLOCKED or NO_TEAM_MEMBERSHIP when denied.
 */
export async function authenticateWithGoogleAccessToken(
  googleAccessToken: string,
  teamId?: string,
): Promise<MemberAuthResult> {
  if (isDevAuthStubEnabled() && googleAccessToken === 'dev-google-member') {
    const token = resolveDevStubToken(DEV_STUB_JWT);
    const peek = peekJwtPayload(token);
    return {
      beatly_token: token,
      manager_email: peek.email,
      team_id: teamId || peek.team_id,
      team_ids: peek.team_ids,
      user_id: peek.user_id,
      display_name: peek.name || peek.email,
      role: peek.role,
    };
  }
  const response = await postDevtoolsAuth<{
    token: string;
    user_id: string;
    team_id: string;
    team_ids: string[];
    email: string;
    display_name: string;
  }>('/v1/auth/google', {
    google_token: googleAccessToken,
    team_id: teamId || null,
  });
  return {
    beatly_token: response.token,
    manager_email: response.email,
    team_id: response.team_id,
    team_ids: response.team_ids,
    user_id: response.user_id,
    display_name: response.display_name,
    role: 'member',
  };
}

/**
 * Refresh / validate an existing member JWT (peek claims locally).
 * Name is historical; no longer calls api-v2 admin endpoints.
 */
export async function refreshAdminToken(token: string): Promise<MemberAuthResult> {
  if (isDevAuthStubEnabled()) {
    const resolved = resolveDevStubToken(token);
    const peek = peekJwtPayload(resolved);
    return {
      beatly_token: resolved,
      manager_email: peek.email,
      team_id: peek.team_id,
      team_ids: peek.team_ids,
      user_id: peek.user_id,
      display_name: peek.name || peek.email,
      role: peek.role,
    };
  }
  // Member JWTs are long-lived; accept stored token and surface claims.
  const peek = peekJwtPayload(token);
  return {
    beatly_token: token,
    manager_email: peek.email,
    team_id: peek.team_id,
    team_ids: peek.team_ids,
    user_id: peek.user_id,
    display_name: peek.name || peek.email,
    role: peek.role,
  };
}

/** Switch active team and remint JWT. */
export async function switchTeam(token: string, teamId: string): Promise<MemberAuthResult> {
  const response = await postDevtoolsAuth<{
    token: string;
    user_id: string;
    team_id: string;
    team_ids: string[];
    email: string;
    display_name: string;
  }>(`/v1/auth/switch-team?team_id=${encodeURIComponent(teamId)}`, {}, token);
  return {
    beatly_token: response.token,
    manager_email: response.email,
    team_id: response.team_id,
    team_ids: response.team_ids,
    user_id: response.user_id,
    display_name: response.display_name,
    role: 'member',
  };
}

/** Peek member session claims from a JWT (local stub or stored token). */
export async function getMemberSessionInfo(token: string): Promise<MemberAuthInfo> {
  const peek = peekJwtPayload(resolveDevStubToken(token));
  return {
    name: peek.name || peek.email,
    email: peek.email,
    team_id: peek.team_id,
    team_ids: peek.team_ids,
    user_id: peek.user_id,
    role: peek.role,
  };
}

/** Load Google profile for the blocked-access message. Does not talk to Beatly APIs. */
export async function fetchGoogleUserInfo(accessToken: string): Promise<GoogleUserInfo> {
  const response = await fetch(
    `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${encodeURIComponent(accessToken)}`,
  );
  const data = (await response.json()) as GoogleUserInfo & {
    error?: string;
    error_description?: string;
  };
  if (!response.ok || data.error) {
    throw createAuthError(data.error_description || 'Failed to get Google user info');
  }
  return { email: data.email, name: data.name, picture: data.picture };
}

function authErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { code?: string }).code;
}

/** DevTools user row is blocked (`USER_BLOCKED`). */
export function isUserBlockedError(error: unknown): boolean {
  return authErrorCode(error) === 'USER_BLOCKED';
}

/** Google login succeeded but the user has no team memberships. */
export function isNoTeamMembershipError(error: unknown): boolean {
  return authErrorCode(error) === 'NO_TEAM_MEMBERSHIP';
}

/** Client-side: JWT peeks without a team_id claim. */
export const NO_MEMBER_SESSION_CODE = 'NO_MEMBER_SESSION';

/** True when the session lacks a usable team_id after local peek. */
export function isNoMemberSessionError(error: unknown): boolean {
  return authErrorCode(error) === NO_MEMBER_SESSION_CODE;
}

/** Fail-closed: require a usable member session (team_id present). */
export async function requireMemberSession(token: string): Promise<MemberAuthInfo> {
  const info = await getMemberSessionInfo(token);
  if (!info.team_id) {
    throw createAuthError('DevTools access denied', 403, NO_MEMBER_SESSION_CODE);
  }
  return info;
}
